import { Component } from '@angular/core';
import { UsersService } from '@cl/core/services';
import { DocumentsService } from '@cl/core/services/documents.service';
import { DATE_TIME_FORMAT } from '@cl/shared/constants';
import { GroupPermissionsType } from '@cl/shared/group-permissions-type.enum';
import { User } from '@cl/shared/models';
import { DocumentFile } from '@cl/shared/models/documents/document-file.model';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { EmployeeNotificationModalComponent } from '@cl/shared/components/employee-notification-modal/employee-notification-modal.component';
import { EmployeeNotificationModalData } from '@cl/shared/models/misc/employee-notification-modal-data.model';
import { HttpParams } from '@angular/common/http';
import { AddSignerToMultipleDocumentFilesDto } from '@cl/shared/models/documents/add-signer-to-multiple-document-files.model';
import { DocumentFileSigner } from '@cl/shared/models/documents/document-file-signer.model';
import { EmployeeDocumentNotification } from '@cl/shared/models/documents/employee-document-notification.model';
import { TableSorters } from '@cl/shared/table-sorters';

@Component({
    selector: 'cl-document-file-signers-modal',
    standalone: false,
    templateUrl: './document-file-signers-modal.component.html',
    styleUrl: './document-file-signers-modal.component.less',
})
export class DocumentFileSignersModalComponent {
    GroupPermissions = GroupPermissionsType;
    DATE_TIME_FORMAT: string = DATE_TIME_FORMAT;
    loading: boolean = false;

    documentFiles: DocumentFile[];
    users: User[];
    multiDocumentFileMode: boolean = false;
    documentFileSigners: DocumentFileSigner[];

    selectedUserId: number;

    constructor(
        private documentsService: DocumentsService,
        private messageService: NzMessageService,
        private modal: NzModalRef,
        private usersService: UsersService,
        private modalService: NzModalService,
    ) {}

    async addDocumentFileSigner(): Promise<void> {
        if (!this.selectedUserId) {
            return;
        }

        try {
            this.loading = true;

            if (!this.documentFileSigners) {
                this.documentFileSigners = [];
            }

            if (this.documentFileSigners.find((dfs: DocumentFileSigner): boolean => dfs.signerUserId == this.selectedUserId)) {
                this.loading = false;
                this.messageService.error('Šis lietotājs jau ir pievienots!');
                return;
            }

            if (this.multiDocumentFileMode) {
                const addSignerToMultipleDocumentFilesData = {
                    documentId: this.documentFiles[0].documentId,
                    signerUserId: this.selectedUserId,
                    documentFileIds: this.documentFiles.map((documentFile: DocumentFile): number => documentFile.id),
                } as AddSignerToMultipleDocumentFilesDto;

                await this.documentsService.addSignerToMultipleDocumentFiles(addSignerToMultipleDocumentFilesData);

                let commonDocumentFileSignersQueryParams: HttpParams = new HttpParams().append('documentId', this.documentFiles[0].documentId);
                this.documentFiles.forEach((documentFile: DocumentFile) => {
                    commonDocumentFileSignersQueryParams = commonDocumentFileSignersQueryParams.append('documentFileIds', documentFile.id);
                });

                this.documentFileSigners = (await this.documentsService.getCommonDocumentFileSigners(commonDocumentFileSignersQueryParams)).data;
                this.loading = false;
                return;
            }

            const documentFile: DocumentFile = this.documentFiles[0];
            this.documentFileSigners.push({
                documentId: documentFile.documentId,
                documentFileId: documentFile.id,
                signerUserId: this.selectedUserId,
            } as DocumentFileSigner);

            this.selectedUserId = null;
            documentFile.documentFileSigners = this.documentFileSigners;

            await this.documentsService.updateDocumentFile(documentFile);

            const updatedDocumentFile = (await this.documentsService.getDocumentFile(documentFile.documentId, documentFile.id)).data;
            this.documentFileSigners = updatedDocumentFile.documentFileSigners;
            this.loading = false;
        } catch (ex) {
            this.loading = false;
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                return;
            }
            this.messageService.error('Kļūda pievienojot dokumenta faila parakstītāju');
            return;
        }
    }

    async deleteDocumentFileSigner(documentFileSignerIndex: number): Promise<void> {
        try {
            this.loading = true;
            if (this.multiDocumentFileMode) {
                const deleteableDocumentFileSigner: DocumentFileSigner = this.documentFileSigners[documentFileSignerIndex];
                if (!deleteableDocumentFileSigner) {
                    return;
                }

                let deleteSignerFromMultipleDocumentFilesQueryParams: HttpParams = new HttpParams()
                    .append('documentId', deleteableDocumentFileSigner.documentId)
                    .append('signerUserId', deleteableDocumentFileSigner.signerUserId);

                this.documentFiles.forEach((documentFile: DocumentFile): void => {
                    deleteSignerFromMultipleDocumentFilesQueryParams = deleteSignerFromMultipleDocumentFilesQueryParams.append(
                        'documentFileIds',
                        documentFile.id,
                    );
                });

                await this.documentsService.deleteSignerFromMultipleDocumentFiles(deleteSignerFromMultipleDocumentFilesQueryParams);

                let commonDocumentFileSignersQueryParams: HttpParams = new HttpParams().append('documentId', this.documentFiles[0].documentId);
                this.documentFiles.forEach((documentFile: DocumentFile) => {
                    commonDocumentFileSignersQueryParams = commonDocumentFileSignersQueryParams.append('documentFileIds', documentFile.id);
                });

                this.documentFileSigners = (await this.documentsService.getCommonDocumentFileSigners(commonDocumentFileSignersQueryParams)).data;
                this.loading = false;
                return;
            }

            const documentFile: DocumentFile = this.documentFiles[0];
            this.documentFileSigners.splice(documentFileSignerIndex, 1);
            documentFile.documentFileSigners = this.documentFileSigners;
            await this.documentsService.updateDocumentFile(documentFile);

            const updatedDocumentFile: DocumentFile = (await this.documentsService.getDocumentFile(documentFile.documentId, documentFile.id)).data;
            this.documentFileSigners = updatedDocumentFile.documentFileSigners;
            this.loading = false;
        } catch (ex) {
            this.loading = false;
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                return;
            }
            this.messageService.error('Kļūda dzēšot dokumenta faila parakstītāju');
            return;
        }
    }

    async sendDocumentSigningNotification(documentFileSigner: DocumentFileSigner): Promise<void> {
        try {
            const modalData: EmployeeNotificationModalData<EmployeeDocumentNotification> = {
                user: documentFileSigner.signerUser,
                notificationModel: {
                    receiverUserId: documentFileSigner.signerUserId,
                    additionalMessage: null,
                    documentId: this.documentFiles[0].documentId,
                    documentFileIds: this.documentFiles.map((df: DocumentFile): number => df.id),
                },
            };

            const modal: NzModalRef<EmployeeNotificationModalComponent<EmployeeDocumentNotification>> = this.modalService.create({
                nzTitle: 'Paziņojums darbiniekam',
                nzContent: EmployeeNotificationModalComponent<EmployeeDocumentNotification>,
                nzData: modalData,
            });

            modal.afterClose.subscribe(async (employeeDocumentNotificationOptions: EmployeeDocumentNotification): Promise<void> => {
                if (!employeeDocumentNotificationOptions) {
                    return;
                }

                try {
                    this.loading = true;
                    await this.documentsService.sendEmployeeDocumentSigningNotification(employeeDocumentNotificationOptions);
                    this.messageService.success('Atgādinājums veiksmīgi nosūtīts');

                    if (this.multiDocumentFileMode) {
                        let commonDocumentFileSignersQueryParams: HttpParams = new HttpParams().append(
                            'documentId',
                            this.documentFiles[0].documentId,
                        );
                        this.documentFiles.forEach((documentFile: DocumentFile) => {
                            commonDocumentFileSignersQueryParams = commonDocumentFileSignersQueryParams.append('documentFileIds', documentFile.id);
                        });

                        this.documentFileSigners = (
                            await this.documentsService.getCommonDocumentFileSigners(commonDocumentFileSignersQueryParams)
                        ).data;

                        this.loading = false;
                        return;
                    }

                    const documentFile = (await this.documentsService.getDocumentFile(this.documentFiles[0].documentId, this.documentFiles[0].id))
                        .data;

                    this.documentFileSigners = documentFile.documentFileSigners;
                    this.loading = false;
                } catch (ex) {
                    this.loading = false;
                    console.error(ex);
                    if (ex.error?.errors) {
                        ex.error.errors.forEach((error) => {
                            this.messageService.error(error);
                        });
                        return;
                    }

                    this.messageService.error('Neizdevās nosūtīt atgādinājumu darbiniekam');
                    return;
                }
            });
        } catch (ex) {
            this.loading = false;
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                return;
            }

            this.messageService.error('Neizdevās nosūtīt atgādinājumu darbiniekam');
            return;
        }
    }

    userToString(user: User): string {
        return this.usersService.toString(user);
    }

    close(): void {
        this.modal.close();
    }

    readonly sortByDocumentFileSignerUser = TableSorters.sortString<DocumentFileSigner>((e) => this.userToString(e.signerUser));
    readonly sortByDocumentFileSignerLastNotificationTimestamp = TableSorters.sortString<DocumentFileSigner>((e) => e.lastNotificationTimestamp);
    readonly sortByDocumentFileSignerSignedTimestamp = TableSorters.sortString<DocumentFileSigner>((e) => e.signedTimestamp);
}
